"use client";

import {
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from "react";
import { WPAPIPost, WPUser } from "@/types";
import { deleteCookie, getCookie, setCookie } from "cookies-next";
import { fetchUserData, fetchUserPosts } from "@/lib/wordpress/users";

export interface UserContextData {
	user: WPUser | null;
	userPosts: WPAPIPost[] | null; // Add userPosts state
	login: (userData: WPUser, jwtToken: string) => void;
	signOut: () => void;
	activePost: WPAPIPost | null;
	setActivePost: (post: WPAPIPost | null) => void;
}

interface WPUserProviderProps {
	children: ReactNode;
}

const UserContext = createContext<UserContextData | null>(null);

const getCachedUser = (): WPUser | null => {
	if (typeof window === "undefined") {
		return null;
	}

	const cachedUser = window.localStorage.getItem("user");
	return cachedUser ? JSON.parse(cachedUser) : null;
};

export function useWPUser(): UserContextData {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error("useUser must be used within a WPUserProvider");
	}
	return context;
}

export function WPUserProvider({ children }: WPUserProviderProps) {
	const [user, setUser] = useState<WPUser | null>(getCachedUser);
	const [activePost, setActivePost] = useState<WPAPIPost | null>(null);
	const [userPosts, setUserPosts] = useState<WPAPIPost[]>([]); // Initialize userPosts as empty array

	const login = (userData: WPUser, jwtToken: string) => {
		setCookie("token", jwtToken, {
			secure: true,
			sameSite: "strict",
			maxAge: 60 * 60 * 24, // 1 day
		});
		setUser(userData);
		if (typeof window !== "undefined") {
			window.localStorage.setItem("user", JSON.stringify(userData));
		}
	};

	const signOut = () => {
		deleteCookie("token");
		if (typeof window !== "undefined") {
			window.localStorage.removeItem("user");
		}
		setUser(null);
	};

	useEffect(() => {
		const token = getCookie("token") as string | undefined;
		if (token && !user) {
			fetchUserData()
				.then((freshUserData) => {
					setUser(freshUserData);
					if (typeof window !== "undefined") {
						window.localStorage.setItem("user", JSON.stringify(freshUserData));
					}
				})
				.catch(() => signOut());
		}
	}, [user]);

	// Fetch user posts whenever user data changes
	useEffect(() => {
		const token = getCookie("token");
		if (user && token) {
			fetchUserPosts(user.id)
				.then((posts) => setUserPosts(posts))
				.catch(() => setUserPosts([]));
		}
	}, [user]);

	return (
		<UserContext.Provider value={{ user, login, signOut, userPosts, activePost, setActivePost }}>
			{children}
		</UserContext.Provider>
	);
}
